import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import type { CCPOfferingContent, CCPPricingPlanTier } from '@/contentful/product-catalog.tsx';
import { RichTextComponent } from '@/libraries/design/utils/rich-text/src/rich-text.tsx';
import {
	getCurrencyFromUserLocale,
	LOCALE_CACHE_KEY,
	type CurrencyISOCode,
} from '@/utils/useGeoCurrency.ts';

import { getPricingTiersTableData } from './pricing-tiers-table-client.ts';
import { pricingTiersTableMessages } from './pricing-tiers-table-messages.ts';

const tableCss = css({
	width: '100%',
	tableLayout: 'fixed',
	borderSpacing: token('space.025'),
	textAlign: 'center',
	color: token('color.text'),
});
const richTextTableCss = css({
	overflowX: 'auto',
});
const currencySelectorCss = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	gap: token('space.100'),
	marginBlockEnd: token('space.100'),
});
const currencySelectCss = css({
	backgroundColor: token('elevation.surface'),
	border: `1px solid ${token('color.border')}`,
	borderRadius: token('radius.small'),
	color: token('color.text'),
	paddingBlock: token('space.050'),
	paddingInline: token('space.100'),
	':focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});
const captionCss = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
});
const linkCss = css({
	color: token('color.link'),
	textDecoration: 'underline',
});
const headingCss = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	paddingInlineStart: token('space.100'),
	paddingInlineEnd: token('space.100'),
	fontWeight: token('font.weight.regular'),
});
const tierCss = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	paddingInlineStart: token('space.100'),
	paddingInlineEnd: token('space.100'),
	fontWeight: token('font.weight.regular'),
});
const priceCss = css({
	backgroundColor: token('color.background.neutral.subtle'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	paddingInlineStart: token('space.100'),
	paddingInlineEnd: token('space.100'),
});

export interface UniformPricingTiersTableProps {
	sku?: string;
	title?: string;
	titleHref?: string;
	billingCycle?: 'MONTH' | 'YEAR';
	unit?: 'user' | 'creator' | 'agent';
	countryCode?: string;
	manualTableOverrideHtml?: string;
}

interface FetchedPricingData {
	sku: string;
	data?: Pick<CCPOfferingContent, 'defaultPricingPlans'>;
}

const UniformPricingTiersTable = (props: UniformPricingTiersTableProps) => {
	const { sku, manualTableOverrideHtml } = props;
	if (manualTableOverrideHtml?.trim()) {
		return (
			<div css={richTextTableCss} data-offering-sku={sku}>
				<RichTextComponent text={manualTableOverrideHtml} />
			</div>
		);
	}
	if (!hasFetchedPricingFields(props)) {
		return null;
	}
	return <FetchedPricingTiersTable {...props} />;
};

export default UniformPricingTiersTable;

type FetchedPricingTiersTableProps = UniformPricingTiersTableProps &
	Required<Pick<UniformPricingTiersTableProps, 'sku' | 'title' | 'titleHref' | 'billingCycle'>>;

const FetchedPricingTiersTable = (props: FetchedPricingTiersTableProps) => {
	const { sku, title, titleHref, billingCycle, unit = 'user', countryCode = 'US' } = props;
	const intl = useIntl();
	const [fetchedPricingData, setFetchedPricingData] = useState<FetchedPricingData>();
	const [currency, setCurrency] = useState<CurrencyISOCode>(getCurrencyFromUserLocale(countryCode));

	useEffect(() => {
		const storedLocale = sessionStorage.getItem(LOCALE_CACHE_KEY);
		if (storedLocale) {
			setCurrency(getCurrencyFromUserLocale(storedLocale));
		}
	}, []);

	useEffect(() => {
		let isCurrent = true;
		void getPricingTiersTableData({ sku })
			.then((data) => {
				if (isCurrent) {
					setFetchedPricingData({ sku, data });
				}
			})
			.catch(() => {
				if (isCurrent) {
					setFetchedPricingData({ sku });
				}
			});

		return () => {
			isCurrent = false;
		};
	}, [sku]);

	const offering = fetchedPricingData?.sku === sku ? fetchedPricingData.data : undefined;
	const matchingPlans = getMatchingPlans({ offering, billingCycle });
	const availableCurrencies: CurrencyISOCode[] = (['USD', 'JPY'] as const).filter((option) =>
		matchingPlans?.some((item) => item.currency === option),
	);
	const selectedCurrency = availableCurrencies.includes(currency)
		? currency
		: (availableCurrencies[0] ?? currency);
	const plan = matchingPlans?.find((item) => item.currency === selectedCurrency);
	const tiers = plan?.items?.[0]?.tiers;
	const tierHeadingByUnit = {
		user: pricingTiersTableMessages.userTierHeading,
		creator: pricingTiersTableMessages.creatorTierHeading,
		agent: pricingTiersTableMessages.agentTierHeading,
	};
	const monthlyPriceByUnit = {
		user: pricingTiersTableMessages.monthlyUserPrice,
		creator: pricingTiersTableMessages.monthlyCreatorPrice,
		agent: pricingTiersTableMessages.monthlyAgentPrice,
	};
	const tierHeading = intl.formatMessage(tierHeadingByUnit[unit]);
	const priceHeading = intl.formatMessage(
		billingCycle === 'MONTH'
			? pricingTiersTableMessages.monthlyPriceHeading
			: pricingTiersTableMessages.annualPriceHeading,
	);

	if (!tiers?.length) {
		return null;
	}

	return (
		<div>
			<label css={currencySelectorCss}>
				<span>{intl.formatMessage(pricingTiersTableMessages.currencyLabel)}</span>
				<select
					css={currencySelectCss}
					value={selectedCurrency}
					onChange={(event) => setCurrency(event.target.value as CurrencyISOCode)}
				>
					{availableCurrencies.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</select>
			</label>
			<table css={tableCss} data-offering-sku={sku}>
				<PricingTiersTableCaption title={title} titleHref={titleHref} />
				<thead>
					<tr>
						<th css={headingCss} scope="col">
							{tierHeading}
						</th>
						<th css={headingCss} scope="col">
							{priceHeading}
						</th>
					</tr>
				</thead>
				<tbody>
					{tiers.map((tier) => (
						<tr key={`${tier.floor}-${tier.ceiling}`}>
							<th css={tierCss} scope="row">
								{formatTierRange(tier)}
							</th>
							<td css={priceCss}>
								{billingCycle === 'MONTH'
									? intl.formatMessage(monthlyPriceByUnit[unit], {
											price: formatTierPrice({ tier, currency: selectedCurrency, billingCycle }),
										})
									: formatTierPrice({ tier, currency: selectedCurrency, billingCycle })}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

interface GetMatchingPlansArgs {
	offering?: FetchedPricingData['data'];
	billingCycle: 'MONTH' | 'YEAR';
}

function getMatchingPlans(args: GetMatchingPlansArgs) {
	const { offering, billingCycle } = args;
	return offering?.defaultPricingPlans?.filter(
		(item) =>
			item.primaryCycle_interval === billingCycle &&
			item.type === 'COMMERCIAL' &&
			item.status === 'ACTIVE' &&
			item.activatedWithReason === 'DEFAULT_PRICING',
	);
}

function hasFetchedPricingFields(
	props: UniformPricingTiersTableProps,
): props is FetchedPricingTiersTableProps {
	const { sku, title, titleHref, billingCycle } = props;
	return Boolean(sku && title && titleHref && billingCycle);
}

interface PricingTiersTableCaptionProps {
	title: string;
	titleHref: string;
}

const PricingTiersTableCaption = (props: PricingTiersTableCaptionProps) => {
	const { title, titleHref } = props;
	return (
		<caption css={captionCss}>
			<a css={linkCss} href={titleHref}>
				{title}
			</a>
		</caption>
	);
};

function formatTierRange(tier: CCPPricingPlanTier): string {
	const floor = Math.max(1, tier.floor);
	const start = new Intl.NumberFormat('en-US').format(floor);
	if (!tier.ceiling || tier.ceiling < floor) {
		return `${start}+`;
	}
	return `${start} - ${new Intl.NumberFormat('en-US').format(tier.ceiling)}`;
}

interface FormatTierPriceArgs {
	tier: CCPPricingPlanTier;
	currency: 'USD' | 'JPY';
	billingCycle: 'MONTH' | 'YEAR';
}

function formatTierPrice(args: FormatTierPriceArgs): string {
	const { tier, currency, billingCycle } = args;
	const amount = tier.flatAmount || tier.unitAmount;
	const value = currency === 'USD' ? amount / 100 : amount;
	const locale = currency === 'JPY' ? 'ja-JP' : 'en-US';
	const fractionDigits = billingCycle === 'MONTH' && currency !== 'JPY' ? 2 : 0;
	return `${currency} ${new Intl.NumberFormat(locale, {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	}).format(value)}`;
}
